import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { ProductTourProvider } from './components/ProductTour';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Pages
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import MarketplacePage from './pages/MarketplacePage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import OrderBuilderPage from './pages/OrderBuilderPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import ExpertSessionPage from './pages/ExpertSessionPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminServices from './pages/admin/AdminServices';
import AdminTeam from './pages/admin/AdminTeam';
import AdminAppointments from './pages/admin/AdminAppointments';
import AdminServiceRequests from './pages/admin/AdminServiceRequests';
import AdminPartnerInquiries from './pages/admin/AdminPartnerInquiries';
import ServiceRequestsPage from './pages/ServiceRequestsPage';
import OnboardingPage from './pages/OnboardingPage';
import DIYToolsPage from './pages/DIYToolsPage';

// Directory Pages (Public)
import DirectoryPage from './pages/DirectoryPage';
import DirectoryCategoryPage from './pages/DirectoryCategoryPage';
import BusinessDetailPage from './pages/BusinessDetailPage';
import DirectoryDealsPage from './pages/DirectoryDealsPage';
import DirectoryClassifiedsPage from './pages/DirectoryClassifiedsPage';
import DirectoryJobsPage from './pages/DirectoryJobsPage';
import DirectoryRegisterPage from './pages/DirectoryRegisterPage';

// Dashboard Publishing Page
import PublishingPage from './pages/PublishingPage';

// Social/Community Pages
import MessagesPage from './pages/MessagesPage';
import BookmarksPage from './pages/BookmarksPage';
import MentionsPage from './pages/MentionsPage';
import TrainingPage from './pages/TrainingPage';

// Cinematic Ads Page
import CinematicAdsPage from './pages/CinematicAdsPage';

// Badge Landing Page
import BadgeLandingPage from './pages/BadgeLandingPage';

// Content Detail Page
import ContentDetailPage from './pages/ContentDetailPage';

// Business Listing Page
import MyBusinessListingPage from './pages/MyBusinessListingPage';

// Tracking Settings Page
import TrackingSettingsPage from './pages/TrackingSettingsPage';
import MFASettingsPage from './pages/MFASettingsPage';

// Gift Portal Page
import GiftPortalPage from './pages/GiftPortalPage';

// AI Besty Link Page
import AIBestyLinkPage from './pages/AIBestyLinkPage';

// Partner Contact Page
import PartnerContactPage from './pages/PartnerContactPage';

// Ranking System Page
import RankingSystemPage from './pages/RankingSystemPage';

// Done For You Projects Page
import DFYProjectsPage from './pages/DFYProjectsPage';

// Team Management Page
import TeamMembersPage from './pages/TeamMembersPage';

// Affiliate Dashboard
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AffiliateMarketplacePage from './pages/AffiliateMarketplacePage';
import AffiliateTrainingPage from './pages/AffiliateTrainingPage';

// Leads Dashboard
import LeadsDashboardPage from './pages/LeadsDashboardPage';

// Analytics Dashboard
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';

// AGI Agent Page
import AgentPage from './pages/AgentPage';

// Security Settings Page
import SecuritySettingsPage from './pages/SecuritySettingsPage';

// Protected Route with Support Chat
const ProtectedRoute = ({ children, adminOnly = false, businessOnly = false }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (adminOnly && user?.role !== 'admin' && user?.role !== 'pod_team_member') {
    return <Navigate to="/dashboard" />;
  }

  // Consumer users cannot access business-only routes
  if (businessOnly && user?.role === 'consumer') {
    return <Navigate to="/dashboard" />;
  }
  
  return (
    <ProductTourProvider>
      {children}
    </ProductTourProvider>
  );
};

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/partners" element={<PartnerContactPage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegisterPage />} />
      <Route path="/forgot-password" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ForgotPasswordPage />} />
      <Route path="/reset-password" element={isAuthenticated ? <Navigate to="/dashboard" /> : <ResetPasswordPage />} />
      
      {/* Directory Routes (Public) */}
      <Route path="/directory" element={<DirectoryPage />} />
      <Route path="/directory/register" element={<DirectoryRegisterPage />} />
      <Route path="/directory/category/:slug" element={<DirectoryCategoryPage />} />
      <Route path="/directory/search" element={<DirectoryCategoryPage />} />
      <Route path="/directory/business/:id" element={<BusinessDetailPage />} />
      <Route path="/directory/deals" element={<DirectoryDealsPage />} />
      <Route path="/directory/classifieds" element={<DirectoryClassifiedsPage />} />
      <Route path="/directory/content/:id" element={<ContentDetailPage />} />
      <Route path="/directory/jobs" element={<DirectoryJobsPage />} />
      
      {/* Badge Landing Page (Public) */}
      <Route path="/badge/:businessId" element={<BadgeLandingPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/cinematic-ads" element={<ProtectedRoute businessOnly><CinematicAdsPage /></ProtectedRoute>} />
      <Route path="/marketplace" element={<ProtectedRoute businessOnly><MarketplacePage /></ProtectedRoute>} />
      <Route path="/marketplace/:serviceId" element={<ProtectedRoute businessOnly><ServiceDetailPage /></ProtectedRoute>} />
      <Route path="/order/:serviceId" element={<ProtectedRoute businessOnly><OrderBuilderPage /></ProtectedRoute>} />
      <Route path="/orders" element={<ProtectedRoute businessOnly><OrdersPage /></ProtectedRoute>} />
      <Route path="/orders/:orderId" element={<ProtectedRoute businessOnly><OrderDetailPage /></ProtectedRoute>} />
      <Route path="/expert-session" element={<ProtectedRoute businessOnly><ExpertSessionPage /></ProtectedRoute>} />
      <Route path="/zoom" element={<Navigate to="/expert-session" />} />
      <Route path="/knowledge" element={<ProtectedRoute businessOnly><KnowledgeBasePage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute businessOnly><BillingPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/service-requests" element={<ProtectedRoute businessOnly><ServiceRequestsPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute businessOnly><OnboardingPage /></ProtectedRoute>} />
      <Route path="/diy-tools" element={<ProtectedRoute businessOnly><DIYToolsPage /></ProtectedRoute>} />
      <Route path="/publishing" element={<ProtectedRoute businessOnly><PublishingPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute businessOnly><AnalyticsDashboardPage /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
      <Route path="/mentions" element={<ProtectedRoute><MentionsPage /></ProtectedRoute>} />
      <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute businessOnly><TrainingPage /></ProtectedRoute>} />
      <Route path="/my-listing" element={<ProtectedRoute businessOnly><MyBusinessListingPage /></ProtectedRoute>} />
      <Route path="/tracking-settings" element={<ProtectedRoute businessOnly><TrackingSettingsPage /></ProtectedRoute>} />
      <Route path="/security/mfa" element={<ProtectedRoute><MFASettingsPage /></ProtectedRoute>} />
      <Route path="/gifts" element={<ProtectedRoute businessOnly><GiftPortalPage /></ProtectedRoute>} />
      <Route path="/ai-besty-link" element={<ProtectedRoute><AIBestyLinkPage /></ProtectedRoute>} />
      <Route path="/ranking" element={<ProtectedRoute businessOnly><RankingSystemPage /></ProtectedRoute>} />
      <Route path="/dfy-projects" element={<ProtectedRoute businessOnly><DFYProjectsPage /></ProtectedRoute>} />
      <Route path="/dfy-projects/:projectId" element={<ProtectedRoute businessOnly><DFYProjectsPage /></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute businessOnly><TeamMembersPage /></ProtectedRoute>} />
      <Route path="/affiliate" element={<ProtectedRoute><AffiliateDashboardPage /></ProtectedRoute>} />
      <Route path="/affiliate/marketplace" element={<ProtectedRoute><AffiliateMarketplacePage /></ProtectedRoute>} />
      <Route path="/affiliate/training" element={<ProtectedRoute><AffiliateTrainingPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute businessOnly><LeadsDashboardPage /></ProtectedRoute>} />
      <Route path="/agent" element={<ProtectedRoute businessOnly><AgentPage /></ProtectedRoute>} />
      <Route path="/settings/security" element={<ProtectedRoute businessOnly><SecuritySettingsPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/services" element={<ProtectedRoute adminOnly><AdminServices /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute adminOnly><AdminTeam /></ProtectedRoute>} />
      <Route path="/admin/appointments" element={<ProtectedRoute adminOnly><AdminAppointments /></ProtectedRoute>} />
      <Route path="/admin/service-requests" element={<ProtectedRoute adminOnly><AdminServiceRequests /></ProtectedRoute>} />
      <Route path="/admin/partner-inquiries" element={<ProtectedRoute adminOnly><AdminPartnerInquiries /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          theme="dark"
          toastOptions={{
            style: {
              background: 'hsl(222 47% 6%)',
              border: '1px solid rgba(255,255,255,0.1)',
            }
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
